import { useState } from 'react'
import { Page, PageHeader } from '@/components/page'
import { router } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useBasePath } from '@/lib/paths'
import { WizardFormationRef } from '@/types'
import { Globe2, Home, TriangleAlert } from 'lucide-react'

type Props = {
  formation: WizardFormationRef & {
    founder_residency: 'us_resident' | 'non_us_resident' | null
    founder_country_id: string | null
  }
  countries: { id: string; code: string; name: string }[]
}

type DialogStep = 'closed' | 'confirm' | 'choose'

export default function ResidenciesEdit({ formation, countries }: Props) {
  const basePath = useBasePath()
  const [step, setStep] = useState<DialogStep>('closed')
  const [submittingValue, setSubmittingValue] = useState<string | null>(null)
  const [residency, setResidency] = useState<'us_resident' | 'non_us_resident'>(formation.founder_residency ?? 'us_resident')
  const [countryId, setCountryId] = useState(formation.founder_country_id ?? '')
  const [error, setError] = useState<string | null>(null)

  const currentLabel = formation.founder_residency === 'us_resident' ? 'in the US' : 'outside the US'

  const save = () => {
    if (submittingValue) return
    if (residency === 'non_us_resident' && !countryId) {
      setError('Choose the country you live in to continue.')
      return
    }
    setSubmittingValue(residency)
    setError(null)
    router.patch(`${basePath}/formations/${formation.id}/wizard/residency`, {
      founder_residency: residency,
      founder_country_id: residency === 'non_us_resident' ? countryId : null,
    }, {
      onError: (errors) => setError(
        typeof errors.founder_residency === 'string'
          ? errors.founder_residency
          : typeof errors.founder_country_id === 'string'
            ? errors.founder_country_id
            : 'Something went wrong — try again.'
      ),
      onFinish: () => setSubmittingValue(null),
    })
  }

  return (
    <WizardLayout
      title="Where you live"
      currentStep="residency"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Where you live' }]}
      guide={
        <WizardGuide title="Where you live">
          <p>
            Where you live changes your address, your identity paperwork, and how we file your EIN.
            Corppy handles the standard setup either way.
          </p>
        </WizardGuide>
      }
    >
      <Page>
        <PageHeader
          title={<>Where you live</>}
          subtitle={<>You told us you live {currentLabel}.</>}
        />

        <Button type="button" variant="outline" onClick={() => setStep('confirm')}>
          Change this
        </Button>

        {error && <p className="mt-3 text-sm text-destructive" role="alert">{error}</p>}

        <Dialog open={step !== 'closed'} onOpenChange={(open) => setStep(open ? 'confirm' : 'closed')}>
          <DialogContent className="sm:max-w-md">
            {step === 'confirm' && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <TriangleAlert className="size-4 text-muted-foreground" />
                    This changes a few other things
                  </DialogTitle>
                  <DialogDescription>
                    Changing where you live updates your address and the identity paperwork we prepare. If you'd
                    already chosen your own business address, we may need to switch you to a Corppy-provided one
                    instead.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('closed')}>
                    Never mind
                  </Button>
                  <Button type="button" className="flex-1" onClick={() => setStep('choose')}>
                    Continue
                  </Button>
                </div>
              </>
            )}

            {step === 'choose' && (
              <>
                <DialogHeader>
                  <DialogTitle>Where do you live?</DialogTitle>
                  <DialogDescription>Pick one — it takes effect right away.</DialogDescription>
                </DialogHeader>
                <RadioGroup
                  value={residency}
                  onValueChange={(value) => setResidency(value as 'us_resident' | 'non_us_resident')}
                  aria-label="Where do you live?"
                  className="grid gap-3"
                >
                  <label
                    htmlFor="residency-edit-us"
                    className="group flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem
                      value="us_resident" id="residency-edit-us"
                      disabled={!!submittingValue}
                    />
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Home className="size-4 text-muted-foreground" /> I live in the US
                      </span>
                      <span className="text-xs text-muted-foreground">We'll prepare your standard US setup.</span>
                    </span>
                  </label>
                  <label
                    htmlFor="residency-edit-non-us"
                    className="group flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem
                      value="non_us_resident" id="residency-edit-non-us"
                      disabled={!!submittingValue}
                    />
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Globe2 className="size-4 text-muted-foreground" /> I live outside the US
                      </span>
                      <span className="text-xs text-muted-foreground">
                        We'll set up your US business address and handle the EIN paperwork.
                      </span>
                    </span>
                  </label>
                </RadioGroup>
                {residency === 'non_us_resident' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="founder-country">Country</Label>
                    <Combobox
                      id="founder-country"
                      options={countries.map((country) => ({ value: country.id, label: country.name }))}
                      value={countryId}
                      onValueChange={setCountryId}
                      placeholder="Choose your country"
                      searchPlaceholder="Search countries…"
                      emptyMessage="No countries found."
                      disabled={!!submittingValue}
                    />
                  </div>
                )}
                <Button type="button" onClick={save} disabled={!!submittingValue}>
                  {submittingValue ? 'Saving…' : 'Save and review from State'}
                </Button>
              </>
            )}
          </DialogContent>
        </Dialog>
    
      </Page>
    </WizardLayout>
  )
}
